import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogHeaderImage } from 'foundations-components/transitional/components/DialogHeaderImage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { MeetingsIcon, EmailIcon } from 'foundations-assets';
import { Image } from 'foundations-components/transitional/components/Image';
import { useMemo, useCallback } from 'react';
import enviro from 'enviro';
import PortalIdParser from 'PortalIdParser';
// @ts-expect-error no types for bender-url
import HelpDeskIllustration from 'bender-url!commerce-tools-ui-lib/images/2023_ServiceHub_Help_Desk_Ticket1.svg';
import { CommerceTool } from '../../types/CommerceTool';
import useLocalizedCommerceToolName from '../../hooks/useLocalizedCommerceToolName';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { trackCommerceSpecialistsInteraction } from '../../usage-tracker/standaloneTrack';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getMeetingLink = toolName => {
  if (enviro.isQa()) {
    return 'https://share.hsformsqa.com/1zFgp_DLWQgeq0pEZuxsGEAem3xey';
  }
  switch (toolName) {
    case CommerceTool.COMMERCE_HOME_POST_ENROLL:
      return 'https://meetings.hubspot.com/commerce-specialist/post-in-app';
    case CommerceTool.COMMERCE_HOME_PRE_ENROLL:
      return 'https://meetings.hubspot.com/commerce-specialist/pre-in-app';
    default:
      return `https://meetings.hubspot.com/commerce-specialist/${toolName}`;
  }
};
const getEmailLink = toolName => {
  if (enviro.isQa()) {
    return 'https://share.hsformsqa.com/1TeRbMoCtT_y2by__Fye5Vge9mlbk';
  }
  switch (toolName) {
    case CommerceTool.COMMERCE_HOME_POST_ENROLL:
    case CommerceTool.COMMERCE_HOME_PRE_ENROLL:
      return 'https://share.hsforms.com/1tVrVPRYCQpuFrFGF1aRJzgbxo5v';
    default:
      return 'https://share.hsforms.com/1lG9PZxGGQ3-Ddu5TXRfiowbxo5v';
  }
};
export let ModalSource;
(function (ModalSource) {
  ModalSource["INDEX_PAGE"] = "indexPage";
  ModalSource["EDITOR_ABANDONED"] = "editorAbandoned";
  ModalSource["COMMERCE_HOME"] = "commerceHome";
})(ModalSource || (ModalSource = {}));
const CommerceSpecialistsContactModal = ({
  toolName,
  source,
  onClose
}) => {
  const localizedToolName = useLocalizedCommerceToolName(toolName);
  const appendUrlParams = useCallback(urlAsString => {
    try {
      const url = new URL(urlAsString);
      if (toolName === CommerceTool.COMMERCE_HOME_POST_ENROLL) {
        url.searchParams.append('pre_post_enrollment', 'post-enrollment');
      } else if (toolName === CommerceTool.COMMERCE_HOME_PRE_ENROLL) {
        url.searchParams.append('pre_post_enrollment', 'pre-enrollment');
      } else {
        url.searchParams.append('commerce_tool', toolName);
      }
      url.searchParams.append('hub_id', String(PortalIdParser.get() || ''));
      url.searchParams.append('origin', source);
      return url.toString();
    } catch (error) {
      console.error(`CommerceSpecialistsContactModal - Failed to parse link as URL`, {
        urlAsString,
        error
      });
      return urlAsString;
    }
  }, [toolName, source]);
  const meetingLinkWithParam = useMemo(() => {
    return appendUrlParams(getMeetingLink(toolName));
  }, [appendUrlParams, toolName]);
  const emailLinkWithParam = useMemo(() => {
    return appendUrlParams(getEmailLink(toolName));
  }, [appendUrlParams, toolName]);
  const handleClick = subAction => {
    trackCommerceSpecialistsInteraction({
      action: 'commerceSpecialistsContactModalInteraction',
      subAction,
      app: toolName,
      source
    });
    onClose();
  };
  return /*#__PURE__*/_jsxs(Modal, {
    use: "info",
    width: 600,
    children: [/*#__PURE__*/_jsx(DialogHeaderImage, {
      offsetBottom: -23,
      offsetTop: 56,
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "190px",
        children: /*#__PURE__*/_jsx(Image, {
          alt: I18n.text('CommerceToolsUiLib.commerceSpecialistsContactModal.illustrationAltText'),
          src: HelpDeskIllustration
        })
      })
    }), /*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "CommerceToolsUiLib.commerceSpecialistsContactModal.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => handleClick('contactModalClosed')
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx("p", {
        style: {
          textAlign: 'left'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "CommerceToolsUiLib.commerceSpecialistsContactModal.body",
          options: {
            localizedToolName
          }
        })
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsxs(Button, {
        use: "primary",
        href: meetingLinkWithParam,
        external: true,
        onClick: () => handleClick('meetingsLinkClicked'),
        children: [/*#__PURE__*/_jsx(MeetingsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "CommerceToolsUiLib.commerceSpecialistsContactModal.meetingButton"
        })]
      }), /*#__PURE__*/_jsxs(Button, {
        use: "secondary",
        href: emailLinkWithParam,
        external: true,
        onClick: () => handleClick('emailLinkClicked'),
        children: [/*#__PURE__*/_jsx(EmailIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "CommerceToolsUiLib.commerceSpecialistsContactModal.contactButton"
        })]
      })]
    })]
  });
};
export default CommerceSpecialistsContactModal;